/**
 * Storage abstraction for persistence.
 *
 * The engine never touches `localStorage` directly. Instead it depends on this
 * tiny synchronous key-value interface, so:
 *   - the browser renderer wires in a localStorage-backed adapter,
 *   - tests (and the headless balance bot) use an in-memory store,
 *   - both behave identically and are fully deterministic.
 *
 * The interface is deliberately minimal (get/set/remove/keys) and string-only,
 * matching the Web Storage subset we actually use. All implementations MUST be
 * fail-safe on read: a backend that throws (quota, privacy mode, disabled
 * storage) should degrade, never crash the game.
 */

export interface KVStorage {
  /** Return the stored string for `key`, or null if absent. Never throws. */
  get(key: string): string | null;
  /** Persist `value` under `key`. May throw on quota; callers handle it. */
  set(key: string, value: string): void;
  /** Remove `key` (no-op if absent). Never throws. */
  remove(key: string): void;
  /** All keys currently present (order unspecified). Never throws. */
  keys(): string[];
}

/**
 * In-memory KV store. Used by tests + the headless bot. Deterministic and
 * isolated — perfect for round-trip/migration/corruption assertions.
 */
export class MemoryStorage implements KVStorage {
  private map = new Map<string, string>();

  constructor(initial?: Record<string, string>) {
    if (initial) {
      for (const k of Object.keys(initial)) this.map.set(k, initial[k]!);
    }
  }

  get(key: string): string | null {
    return this.map.has(key) ? this.map.get(key)! : null;
  }

  set(key: string, value: string): void {
    this.map.set(key, value);
  }

  remove(key: string): void {
    this.map.delete(key);
  }

  keys(): string[] {
    return [...this.map.keys()];
  }

  /** Test convenience: how many entries are stored. */
  get size(): number {
    return this.map.size;
  }
}

/**
 * Minimal shape of the Web Storage API we rely on. Declared locally so the
 * engine doesn't need a hard DOM lib dependency at runtime.
 */
export interface WebStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  readonly length: number;
  key(index: number): string | null;
}

/**
 * Adapter over a Web Storage object (browser `localStorage`/`sessionStorage`).
 * Reads + key enumeration are wrapped so a throwing backend degrades to
 * "empty"/no-op instead of crashing; writes propagate (so callers can react to
 * a real quota error if they choose).
 */
export class WebStorageAdapter implements KVStorage {
  constructor(private readonly backing: WebStorageLike) {}

  get(key: string): string | null {
    try {
      return this.backing.getItem(key);
    } catch {
      return null;
    }
  }

  set(key: string, value: string): void {
    this.backing.setItem(key, value);
  }

  remove(key: string): void {
    try {
      this.backing.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  keys(): string[] {
    try {
      const out: string[] = [];
      for (let i = 0; i < this.backing.length; i++) {
        const k = this.backing.key(i);
        if (k != null) out.push(k);
      }
      return out;
    } catch {
      return [];
    }
  }
}

/**
 * Resolve the best available storage for the current environment.
 *  - In a browser with a working `localStorage`, returns a WebStorageAdapter.
 *  - Otherwise (Node, SSR, privacy mode that throws on access), returns a
 *    MemoryStorage so the game still runs (saves just won't persist).
 * Never throws.
 */
export function defaultStorage(): KVStorage {
  try {
    const g = globalThis as unknown as { localStorage?: WebStorageLike };
    const ls = g.localStorage;
    if (ls) {
      // Probe it actually works (some environments expose but throw on use).
      const probe = "__nullrun_probe__";
      ls.setItem(probe, "1");
      ls.removeItem(probe);
      return new WebStorageAdapter(ls);
    }
  } catch {
    /* fall through to memory */
  }
  return new MemoryStorage();
}
